import ApplicationError from 'es/app/business-logic/ApplicationError';
import { IFacilityDetails } from 'es/app/business-logic/domain-models/Facility';
import { ICompany } from 'es/app/business-logic/domain-models/ICompany';
import Guid, { isNullOrEmpty } from 'es/core/types/Guid';
import { CREATE_COMPANY } from './graphql/system.createCompany';
import { CREATE_FACILITY } from './graphql/company.createFacility';
import ApolloProvider from 'es/core/net/ApolloProvider';

import i18n from 'es/i18n';
import {
    CreateCompanyMutation,
    CreateCompanyMutationVariables,
    CreateFacilityInput,
    CreateFacilityMutation,
    CreateFacilityMutationVariables,
    MapProvider,
} from 'es/app/__generated__/global';
const t = (key: string) => i18n.t(`app/business-logic/services:facility.createFacility.${key}`);

export interface ICreateFacilityResult {
    companyId: Guid;
    facilityId: Guid | null;
    facilityCode: Guid | null;
}

export async function createFacility(company: ICompany, facility: IFacilityDetails) {
    const createFacilityResult: ICreateFacilityResult = {
        companyId: company.id,
        facilityId: null,
        facilityCode: null,
    };

    if (!company.id) {
        const createdCompanyId = await getOrCreateCompanyId(company);

        if (!createdCompanyId) {
            ApplicationError.raiseError(t('errors.createCompanyFailed'));
            return createFacilityResult;
        }

        createFacilityResult.companyId = createdCompanyId;
    }
    const facilityInput: CreateFacilityInput = {
        companyId: createFacilityResult.companyId,
        name: facility.name,
        alias: facility.alias,
        windowsTimeZone: facility.windowsTimeZoneId ?? '',
        locale: facility.locale ?? 'en-AU',
        passwordResetFrequencyDays: facility.passwordResetFrequencyDays,
        smaxCustomerCode: facility.smaxCustomerCode,
        smaxStaleDataTimeoutSeconds: facility.smaxStaleDataTimeoutSeconds,
        region: facility.region,
        processDataRetentionInDays: facility.processDataRetentionInDays,
        type: facility.type,
        roadMapProvider: facility.roadMapProvider ?? MapProvider.Mapbox,
        satelliteMapProvider: facility.satelliteMapProvider ?? MapProvider.Azure,
        isActive: facility.isActive,
        useImperialUnits: facility.useImperialUnits,
    };
    const mutationResult = await ApolloProvider.company(company.alias).mutate<
        CreateFacilityMutation,
        CreateFacilityMutationVariables
    >({
        mutation: CREATE_FACILITY,
        variables: {
            facility: facilityInput,
        },
    });

    const result = mutationResult?.data?.company?.facility?.create;

    if (result) {
        createFacilityResult.facilityId = result.id;
        createFacilityResult.facilityCode = result.facilityCode;
    } else {
        throw new Error('Failed to create facility');
    }

    return createFacilityResult;
}

async function getOrCreateCompanyId(company: ICompany): Promise<Guid | undefined> {
    if (!isNullOrEmpty(company.id)) {
        return company.id;
    }

    const { data } = await ApolloProvider.system.mutate<CreateCompanyMutation, CreateCompanyMutationVariables>({
        mutation: CREATE_COMPANY,
        variables: {
            company: {
                name: company.name,
                alias: company.alias,
            },
        },
    });

    const result = data?.system?.company?.create;
    return result?.id;
}
